import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Download,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Car,
  Truck,
  Info,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VEHICLE_TYPES = [
  { value: 'car', label: 'Light Vehicle', icon: Car },
  { value: '3w', label: '3-Wheeler', icon: Car },
  { value: 'truck', label: 'Truck', icon: Truck },
  { value: 'bus', label: 'Bus', icon: Truck },
];

export default function BulkUploadPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [vehicleType, setVehicleType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  const handleDownloadTemplate = async () => {
    if (!vehicleType) {
      toast.error('Please select a vehicle type first');
      return;
    }
    
    setDownloadingTemplate(true);
    try {
      const response = await fetch(
        `${API_URL}/api/inspections/bulk-upload/template?vehicle_type=${vehicleType}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to download template');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulk_inspection_template_${vehicleType}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (err) {
      console.error('Template download failed:', err);
      toast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setSelectedFile(file);
      setUploadResults(null);
    }
  };

  const handleUpload = async () => {
    if (!vehicleType) {
      toast.error('Please select a vehicle type');
      return;
    }
    
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    setUploading(true);
    setUploadResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await fetch(
        `${API_URL}/api/inspections/bulk-upload?vehicle_type=${vehicleType}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Upload failed');
      }
      
      setUploadResults(data);
      
      if (data.created_count > 0 && data.error_count === 0) {
        toast.success(`Successfully created ${data.created_count} inspections!`);
      } else if (data.created_count > 0) {
        toast.warning(`Created ${data.created_count} inspections with ${data.error_count} errors`);
      } else {
        toast.error('No inspections were created. Please check the errors below.');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const vehicleTypeLabel = VEHICLE_TYPES.find(v => v.value === vehicleType)?.label || '';

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-4" data-testid="bulk-upload-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Bulk Inspection Upload</h1>
          <p className="text-muted-foreground">
            Upload multiple vehicle inspections at once
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">Bulk Upload Information</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Available for <strong>Light Vehicles, 3-Wheelers, Trucks, and Buses</strong> only</li>
              <li>Not available for Trailers or CNG Storage facilities</li>
              <li>All uploaded inspections will be automatically marked as <strong>APPROVED</strong></li>
              <li>Visual and Gas Leak tests will be marked as <strong>PASS</strong></li>
              <li>Duplicate registrations and cylinder serials will be rejected</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Step 1: Select Vehicle Type */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-sm font-bold">1</span>
            Select Vehicle Type
          </CardTitle>
          <CardDescription>
            Choose the type of vehicles you're uploading
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {VEHICLE_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <div
                  key={type.value}
                  className={cn(
                    "flex flex-col items-center gap-2 p-4 border rounded-lg cursor-pointer transition-all hover:border-primary",
                    vehicleType === type.value && "border-primary bg-primary/5 ring-2 ring-primary/20"
                  )}
                  onClick={() => {
                    setVehicleType(type.value);
                    setUploadResults(null);
                  }}
                  data-testid={`vehicle-type-${type.value}`}
                >
                  <Icon className={cn(
                    "h-8 w-8",
                    vehicleType === type.value ? "text-primary" : "text-muted-foreground"
                  )} />
                  <span className={cn(
                    "text-sm font-medium text-center",
                    vehicleType === type.value ? "text-primary" : "text-muted-foreground"
                  )}>
                    {type.label}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Download Template */}
      <Card className={!vehicleType ? "opacity-50" : ""}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-sm font-bold">2</span>
            Download Template
          </CardTitle>
          <CardDescription>
            Download the Excel template for {vehicleTypeLabel || 'your selected vehicle type'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleDownloadTemplate}
            disabled={!vehicleType || downloadingTemplate}
            className="w-full md:w-auto"
            data-testid="download-template-btn"
          >
            {downloadingTemplate ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Download {vehicleTypeLabel} Template
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Fill in the template with your inspection data. Required fields are marked with *.
          </p>
        </CardContent>
      </Card>

      {/* Step 3: Upload File */}
      <Card className={!vehicleType ? "opacity-50" : ""}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-sm font-bold">3</span>
            Upload Completed File
          </CardTitle>
          <CardDescription>
            Upload your filled template to create inspections
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={!vehicleType || uploading}
                className="cursor-pointer"
                data-testid="file-input"
              />
            </div>
            {selectedFile && (
              <Badge variant="secondary" className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4" />
                {selectedFile.name}
              </Badge>
            )}
          </div>
          
          <Button
            onClick={handleUpload}
            disabled={!vehicleType || !selectedFile || uploading}
            className="w-full md:w-auto bg-emerald-600 hover:bg-emerald-700"
            data-testid="upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload & Create Inspections
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Upload Results */}
      {uploadResults && (
        <Card className={uploadResults.error_count > 0 ? "border-amber-300" : "border-emerald-300"}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {uploadResults.error_count === 0 ? (
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              )}
              Upload Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-2xl font-bold">{uploadResults.total_rows}</p>
                <p className="text-sm text-muted-foreground">Total Rows</p>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                <p className="text-2xl font-bold text-emerald-600">{uploadResults.created_count}</p>
                <p className="text-sm text-emerald-700">Created</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{uploadResults.error_count}</p>
                <p className="text-sm text-red-700">Errors</p>
              </div>
            </div>

            {/* Success Table */}
            {uploadResults.success.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-emerald-700">
                  <CheckCircle className="h-4 w-4" />
                  Successfully Created ({uploadResults.success.length})
                </h4>
                <div className="border rounded-lg overflow-hidden max-h-[200px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Row</TableHead>
                        <TableHead>Registration</TableHead>
                        <TableHead>Certificate #</TableHead>
                        <TableHead>Valid Until</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {uploadResults.success.slice(0, 10).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.row}</TableCell>
                          <TableCell className="font-mono">{item.registration}</TableCell>
                          <TableCell className="font-mono text-xs">{item.certificate_number}</TableCell>
                          <TableCell>{item.valid_until}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {uploadResults.success.length > 10 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      ... and {uploadResults.success.length - 10} more
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Error Table */}
            {uploadResults.errors.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-red-700">
                  <XCircle className="h-4 w-4" />
                  Errors ({uploadResults.errors.length})
                </h4>
                <div className="border border-red-200 rounded-lg overflow-hidden max-h-[200px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-red-50">
                        <TableHead>Row</TableHead>
                        <TableHead>Registration</TableHead>
                        <TableHead>Error(s)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {uploadResults.errors.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.row}</TableCell>
                          <TableCell className="font-mono">{item.registration}</TableCell>
                          <TableCell className="text-red-600 text-sm">
                            {item.errors.join('; ')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Actions */}
            {uploadResults.created_count > 0 && (
              <div className="flex gap-3">
                <Button onClick={() => navigate('/inspections')} variant="outline">
                  View All Inspections
                </Button>
                <Button onClick={() => {
                  setSelectedFile(null);
                  setUploadResults(null);
                }}>
                  Upload Another File
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
